import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import { ArrowTopRightOnSquareIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { stringify } from 'query-string';
import { useHistory } from 'react-router';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { fetchInfinite } from '@dashboard/services/request';
import Spinner from '@dashboard/components/Spinner';

const BrowserPreviewWrapper = ({ children, previewUrl, onOpenNewTab }) => (
  <div className="h-full">
    <div className="bg-white border border-gray-100 rounded-lg overflow-hidden h-full shadow">
      <div className="relative flex items-center justify-between bg-white px-2 py-1 border-b border-gray-100 rounded-t-lg">
        <div className="absolute left-2 top-[7px] flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
        </div>

        <p className="text-gray-400 text-xs mx-auto w-2/3 truncate text-center">
          {previewUrl || '\u00A0'}
        </p>

        <button onClick={onOpenNewTab} className="absolute right-2 p-1 hover:bg-gray-100 rounded">
          <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      <div className="h-[calc(100%-2rem)]">{children}</div>
    </div>
  </div>
);

function getEmailPreviewSrc(mailTemplatesUrl, page) {
  switch (page.type) {
    case 'form':
      return `${mailTemplatesUrl}/submission-confirmation-email/${page._id}`;

    case 'checkout':
      if (page.product?.type === 'charge') {
        return `${mailTemplatesUrl}/payment-confirmation-email/${page._id}`;
      }

      if (page.product?.type === 'subscription') {
        return `${mailTemplatesUrl}/subscription-confirmation-email/${page._id}`;
      }

      break;
    case 'event':
      return `${mailTemplatesUrl}/event-confirmation-email/${page._id}`;
    default:
      return ``;
  }
}

export default function PageCompare() {
  const { token } = useAuth();
  const history = useHistory();
  const query = useUrlQuery();
  const { search } = useSearchInput();

  const [pageType] = useState(query.pageType || '');
  const [usageType] = useState(query.usageType || '');
  const [hostedLayoutType, setHostedLayoutType] = useState(query.hostedLayoutType || '');
  const [siteLayoutType, setSiteLayoutType] = useState(query.siteLayoutType || '');
  const [sortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection] = useState(query.sortDirection || -1);
  const queryClient = useQueryClient();
  const [actualPageNumber, setActualPageNumber] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (usageType === 'hosted') setSiteLayoutType('');
    if (usageType === 'site') setHostedLayoutType('');
  }, [usageType]);

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        search,
        ...(pageType && { pageType }),
        ...(usageType && { usageType }),
        ...(hostedLayoutType && { hostedLayoutType }),
        ...(siteLayoutType && { siteLayoutType }),
      }),
    });
  }, [sortBy, sortDirection, search, pageType, usageType, hostedLayoutType, siteLayoutType]);

  const { data, isError, isLoading } = useInfiniteQuery({
    queryKey: [
      'pages-compare',
      sortBy,
      sortDirection,
      search,
      pageType,
      usageType,
      hostedLayoutType,
      siteLayoutType,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await fetchInfinite(
        `/admin/pages?${stringify({
          sort: JSON.stringify({
            [sortBy]: sortDirection,
          }),
          search,
          ...(pageType && { type: pageType }),
          ...(usageType && { usageType }),
          ...(hostedLayoutType && { hostedLayoutType }),
          ...(siteLayoutType && { siteLayoutType }),
          limit: 1,
          skip: pageParam,
        })}`,
        token,
      );

      // Store total on first load
      if (pageParam === 0) {
        setTotal(response.total);
      }

      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const pages = data?.pages?.[0]?.data || [];
  const currentPage = pages[0];

  const handleRandom = async () => {
    const randomIndex = Math.floor(Math.random() * total);

    const newUrl = `/admin/pages?${stringify({
      sort: JSON.stringify({
        [sortBy]: sortDirection,
      }),
      search,
      ...(pageType && { type: pageType }),
      ...(usageType && { usageType }),
      ...(hostedLayoutType && { hostedLayoutType }),
      ...(siteLayoutType && { siteLayoutType }),
      limit: 1,
      skip: randomIndex,
    })}`;

    const response = await fetchInfinite(newUrl, token);

    queryClient.setQueryData(
      [
        'pages-compare',
        sortBy,
        sortDirection,
        search,
        pageType,
        usageType,
        hostedLayoutType,
        siteLayoutType,
      ],
      {
        pages: [response],
        pageParams: [randomIndex],
      },
    );

    setActualPageNumber(randomIndex + 1);
  };

  // Load total on first mount
  useEffect(() => {
    fetchInfinite(
      `/admin/pages?${stringify({
        sort: JSON.stringify({
          [sortBy]: sortDirection,
        }),
        search,
        ...(pageType && { type: pageType }),
        ...(usageType && { usageType }),
        ...(hostedLayoutType && { hostedLayoutType }),
        ...(siteLayoutType && { siteLayoutType }),
        limit: 1,
      })}`,
      token,
    ).then(response => {
      setTotal(response.total);
    });
  }, [sortBy, sortDirection, search, pageType, usageType, hostedLayoutType, siteLayoutType]);

  /* // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPageIndex, pages.length]); */

  if (isLoading) {
    return (
      <div className="p-4 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return <div className="p-4">Failed to load pages. Please try again.</div>;
  }

  if (!pages.length) {
    return <div className="p-4">No pages found</div>;
  }

  const localBaseUrl = 'https://checkoutpage.dev';
  const prodBaseUrl = 'https://checkoutpage.com';
  const localEmailBaseUrl = 'https://api.checkoutpage.dev/mail-templates';
  const prodEmailBaseUrl = 'https://api.checkoutpage.com/mail-templates';

  const localCheckoutPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${localBaseUrl}/checkout/${currentPage.seller._id}/${currentPage._id}?testing=true&preview=true`
      : '';

  const prodCheckoutPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${prodBaseUrl}/checkout/${currentPage.seller._id}/${currentPage._id}?testing=true&preview=true`
      : '';

  const localEmbedPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${localBaseUrl}/embed/${currentPage.seller._id}/${currentPage._id}?testing=true&preview=true`
      : '';

  const prodEmbedPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${prodBaseUrl}/embed/${currentPage.seller._id}/${currentPage._id}?testing=true&preview=true`
      : '';

  const localConfirmationPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${localBaseUrl}/checkout/${currentPage.seller._id}/${
          currentPage._id
        }/confirmation/${Math.random()}?testing=true&preview=true`
      : '';

  const prodConfirmationPreviewUrl =
    currentPage?.seller?._id && currentPage?._id
      ? `${prodBaseUrl}/checkout/${currentPage.seller._id}/${
          currentPage._id
        }/confirmation/${Math.random()}?testing=true&preview=true`
      : '';

  const localEmailPreviewUrl =
    currentPage?._id && getEmailPreviewSrc(localEmailBaseUrl, currentPage);

  const prodEmailPreviewUrl = currentPage?._id && getEmailPreviewSrc(prodEmailBaseUrl, currentPage);

  return (
    <div className="p-4">
      <div className="mb-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* <button
              onClick={handlePrevious}
              disabled={currentPageIndex <= 0}
              className={clsx(
                'p-2 rounded-full hover:bg-gray-100',
                currentPageIndex <= 0 && 'opacity-50 cursor-not-allowed'
              )}
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <button
              onClick={handleNext}
              disabled={currentPageIndex >= pages.length - 1}
              className={clsx(
                'p-2 rounded-full hover:bg-gray-100',
                currentPageIndex >= pages.length - 1 && 'opacity-50 cursor-not-allowed'
              )}
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button> */}
            <div className="text-sm text-gray-600">
              <span>
                Page {actualPageNumber} of {total}
              </span>

              {currentPage && (
                <span className="ml-2 font-medium">({currentPage.name || 'Unnamed Page'})</span>
              )}
            </div>

            <button
              onClick={handleRandom}
              className="p-2 rounded hover:bg-gray-100 flex items-center gap-2"
              title="View random page"
            >
              Load new page <ArrowPathIcon className="w-4 h-4" />
            </button>
          </div>

          {/* <div className="flex items-end gap-x-2">
            <FormElement className="!mb-0">
              <Label htmlFor="pageType">Page type</Label>
              <Select
                name="pageType"
                label="Page type"
                onChange={e => setPageType(e.target.value)}
                className="w-32"
                value={pageType}
              >
                <option value="">All</option>
                <option value="checkout">Checkout</option>
                <option value="event">Event</option>
                <option value="form">Form</option>
              </Select>
            </FormElement>

            <FormElement className="!mb-0">
              <Label htmlFor="usageType">Usage type</Label>
              <Select
                name="usageType"
                label="Usage type"
                onChange={e => setUsageType(e.target.value)}
                className="w-32"
                value={usageType}
              >
                <option value="">All</option>
                <option value="hosted">Hosted</option>
                <option value="site">Site</option>
              </Select>
            </FormElement>

            {usageType === 'hosted' && (
              <FormElement className="!mb-0">
                <Label htmlFor="hostedLayoutType">Hosted layout type</Label>
                <Select
                  name="hostedLayoutType"
                  label="Hosted layout type"
                  onChange={e => setHostedLayoutType(e.target.value)}
                  className="w-32"
                  value={hostedLayoutType}
                >
                  <option value="">All</option>
                  <option value="double">Double</option>
                  <option value="center">Center</option>
                  <option value="split">Split</option>
                </Select>
              </FormElement>
            )}

            {usageType === 'site' && (
              <FormElement className="!mb-0">
                <Label htmlFor="siteLayoutType">Site layout type</Label>
                <Select
                  name="siteLayoutType"
                  label="Site layout type"
                  onChange={e => setSiteLayoutType(e.target.value)}
                  className="w-32"
                  value={siteLayoutType}
                >
                  <option value="">All</option>
                  <option value="embed">Embed</option>
                  <option value="popup">Pop-up</option>
                </Select>
              </FormElement>
            )}

            <FormElement className="!mb-0">
              <Label htmlFor="search">Search</Label>
              <div className="w-64">
                <TextInput
                  border
                  name="search"
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="Search by page name"
                  value={searchInput}
                />
              </div>
            </FormElement>
          </div> */}
        </div>
      </div>

      <div className="h-[calc(100vh-300px)]">
        <TabGroup className="h-full">
          <TabList className="flex justify-center mb-4">
            <Tab
              className={({ selected }) =>
                clsx(
                  'outline-none group flex items-center space-x-2.5 rounded-md px-4 py-2 text-sm font-medium transition-all duration-300 hover:text-chocolate-600',
                  {
                    'text-chocolate-600 bg-gray-50': selected,
                    'text-gray-900 bg-transparent': !selected,
                  },
                )
              }
            >
              Hosted checkout
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'outline-none group flex items-center space-x-2.5 rounded-md px-4 py-2 text-sm font-medium transition-all duration-300 hover:text-chocolate-600',
                  {
                    'text-chocolate-600 bg-gray-50': selected,
                    'text-gray-900 bg-transparent': !selected,
                  },
                )
              }
            >
              Checkout embed
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'outline-none group flex items-center space-x-2.5 rounded-md px-4 py-2 text-sm font-medium transition-all duration-300 hover:text-chocolate-600',
                  {
                    'text-chocolate-600 bg-gray-50': selected,
                    'text-gray-900 bg-transparent': !selected,
                  },
                )
              }
            >
              Confirmation
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'outline-none group flex items-center space-x-2.5 rounded-md px-4 py-2 text-sm font-medium transition-all duration-300 hover:text-chocolate-600',
                  {
                    'text-chocolate-600 bg-gray-50': selected,
                    'text-gray-900 bg-transparent': !selected,
                  },
                )
              }
            >
              Email
            </Tab>
          </TabList>
          <TabPanels className="h-[calc(100%-3.5rem)]">
            <TabPanel className="h-full">
              <div className="grid grid-cols-2 gap-4 h-full">
                <BrowserPreviewWrapper
                  previewUrl={localCheckoutPreviewUrl}
                  onOpenNewTab={() =>
                    localCheckoutPreviewUrl && window.open(localCheckoutPreviewUrl, '_blank')
                  }
                >
                  {localCheckoutPreviewUrl && (
                    <iframe src={localCheckoutPreviewUrl} className="w-full h-full" />
                  )}
                </BrowserPreviewWrapper>

                <BrowserPreviewWrapper
                  previewUrl={prodCheckoutPreviewUrl}
                  onOpenNewTab={() =>
                    prodCheckoutPreviewUrl && window.open(prodCheckoutPreviewUrl, '_blank')
                  }
                >
                  {prodCheckoutPreviewUrl && (
                    <iframe src={prodCheckoutPreviewUrl} className="w-full h-full" />
                  )}
                </BrowserPreviewWrapper>
              </div>
            </TabPanel>

            <TabPanel className="h-full">
              <div className="grid grid-cols-2 gap-4 h-full">
                <BrowserPreviewWrapper
                  previewUrl={localEmbedPreviewUrl}
                  onOpenNewTab={() =>
                    localEmbedPreviewUrl && window.open(localEmbedPreviewUrl, '_blank')
                  }
                >
                  {localEmbedPreviewUrl && (
                    <iframe src={localEmbedPreviewUrl} className="w-full h-full" />
                  )}
                </BrowserPreviewWrapper>

                <BrowserPreviewWrapper
                  previewUrl={prodEmbedPreviewUrl}
                  onOpenNewTab={() =>
                    prodEmbedPreviewUrl && window.open(prodEmbedPreviewUrl, '_blank')
                  }
                >
                  {prodEmbedPreviewUrl && (
                    <iframe src={prodEmbedPreviewUrl} className="w-full h-full" />
                  )}
                </BrowserPreviewWrapper>
              </div>
            </TabPanel>

            <TabPanel className="h-full">
              <div className="grid grid-cols-2 gap-4 h-full">
                <BrowserPreviewWrapper
                  previewUrl={localConfirmationPreviewUrl}
                  onOpenNewTab={() =>
                    localConfirmationPreviewUrl &&
                    window.open(localConfirmationPreviewUrl, '_blank')
                  }
                >
                  {localConfirmationPreviewUrl && (
                    <iframe src={localConfirmationPreviewUrl} className="w-full h-full" />
                  )}
                </BrowserPreviewWrapper>

                <BrowserPreviewWrapper
                  previewUrl={prodConfirmationPreviewUrl}
                  onOpenNewTab={() =>
                    prodConfirmationPreviewUrl && window.open(prodConfirmationPreviewUrl, '_blank')
                  }
                >
                  {prodConfirmationPreviewUrl && (
                    <iframe src={prodConfirmationPreviewUrl} className="w-full h-full" />
                  )}
                </BrowserPreviewWrapper>
              </div>
            </TabPanel>

            <TabPanel className="h-full">
              <div className="grid grid-cols-2 gap-4 h-full">
                <BrowserPreviewWrapper
                  previewUrl={localEmailPreviewUrl}
                  onOpenNewTab={() =>
                    localEmailPreviewUrl && window.open(localEmailPreviewUrl, '_blank')
                  }
                >
                  {currentPage?._id && (
                    <iframe
                      src={localEmailPreviewUrl}
                      className={clsx(
                        'origin-top-left',
                        'scale-50 w-[200%] h-[calc(200%)]',
                        'xl:scale-75 xl:w-[133.33%] xl:h-[133.33%]',
                        '3xl:scale-100 3xl:w-full 3xl:h-full',
                      )}
                    />
                  )}
                </BrowserPreviewWrapper>

                <BrowserPreviewWrapper
                  previewUrl={prodEmailPreviewUrl}
                  onOpenNewTab={() =>
                    prodEmailPreviewUrl && window.open(prodEmailPreviewUrl, '_blank')
                  }
                >
                  {currentPage?._id && (
                    <iframe
                      src={prodEmailPreviewUrl}
                      className={clsx(
                        'origin-top-left',
                        'scale-50 w-[200%] h-[calc(200%)]',
                        'xl:scale-75 xl:w-[133.33%] xl:h-[133.33%]',
                        '3xl:scale-100 3xl:w-full 3xl:h-full',
                      )}
                    />
                  )}
                </BrowserPreviewWrapper>
              </div>
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>
    </div>
  );
}
